import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoEye } from "react-icons/io5";
import { FiEdit2, FiFilter, FiPlusCircle, FiRotateCcw, FiSearch } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useCategoryProducts, useProducts } from "../useHook";
import Loader from "../../../Utils/loader";
import Swal from "sweetalert2";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import SimilarItemsModal from "../../../Components/Modals/SimilarItems/SimilarItems";

function CategoryView() {
  const navigate = useNavigate();
  const { getCategoryProductsWithPagination, searchCategoryProducts } =
    useCategoryProducts();
  const { deleteProduct } = useProducts();
  const [products, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { id } = location.state || {};

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [nameQuery, setNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedProductForSimilar, setSelectedProductForSimilar] =
    useState(null);

  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.Products.READ);
  const canEdit = hasPermission(SystemPermissions.Products.CREATE);
  const canDelete = hasPermission(SystemPermissions.Products.DELETE);
  const canSimilar = hasPermission(
    SystemPermissions.Products.ADD_RELATED_SIMILAR_PRODUCT,
  );
  const showActions = canRead || canDelete || canSimilar || canEdit;

  const fetchCategoryProducts = async (
    categoryId,
    currentOffset = offset,
    showLoader = true,
  ) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getCategoryProductsWithPagination(
        categoryId,
        setCategoryProducts,
        setTotal,
        currentOffset,
        limit,
        nameQuery,
        statusFilter,
      );
    } catch (error) {
      console.error("Error fetching category products:", error);
      Swal.fire("Error", "Failed to fetch category products", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchCategoryProducts(
        id,
        nameQuery,
        statusFilter,
        0,
        limit,
      );
      setCategoryProducts(response?.products || []);
      const totalRecords =
        response?.pagination?.totalRecords || response?.products?.length || 0;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNameQuery("");
    setStatusFilter("");
    setOffset(0);
    fetchCategoryProducts(id, 0);
  };

  useEffect(() => {
    if (id) {
      fetchCategoryProducts(id, offset, true);
    }
  }, [id, offset]);

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    setOffset(newOffset);
    fetchCategoryProducts(id, newOffset, false);
  };

  const handleViewPage = (productId) => {
    navigate("/products/view", {
      state: {
        id: productId,
        returnUrl: window.location.pathname + window.location.search,
      },
    });
  };

  const handleEditProductClick = (productId) => {
    navigate("/products/edit", {
      state: {
        id: productId,
        returnUrl: window.location.pathname + window.location.search,
      },
    });
  };

  const handleDelete = async (productId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await deleteProduct(productId);
      await fetchCategoryProducts(id, offset, false);
      Swal.fire("Deleted!", "Product deleted successfully.", "success");
    } catch (error) {
      console.error("Failed to delete product:", error);
      Swal.fire("Error!", "Failed to delete product. Please try again.", "error");
    }
  };

  if (isLoading) {
    return <Loader message="Loading category products..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Category Products</h2>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by product name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>

        <select
          className="status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Price</th>
                <th className="table-th">Status</th>
                {showActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {products?.length > 0 ? (
                products.map((product, index) => (
                  <tr
                    key={product.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">{product.name}</td>
                    <td className="table-td">
                      {product.currency} {product.price}
                    </td>
                    <td className="table-td">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          product.status
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {product.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {showActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canSimilar && (
                            <button
                              className="btn btn-action"
                              onClick={() => {
                                setSelectedProductForSimilar(product.id);
                                setIsSimilarModalOpen(true);
                              }}
                            >
                              <FiPlusCircle className="text-white" />
                              Similar Items
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() => handleEditProductClick(product.id)}
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canRead && (
                            <button
                              className="btn btn-view"
                              onClick={() => handleViewPage(product.id)}
                            >
                              <IoEye className="text-white" /> View
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(product.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={showActions ? 5 : 4}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedProductForSimilar(null);
          }}
          sourceId={selectedProductForSimilar}
          sourceType="shop"
        />
      )}
    </div>
  );
}

export default CategoryView;
